import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useBlocker, useNavigate } from "react-router-dom";
import { Sync } from "@mui/icons-material";
import { Divider, FormControl, IconButton, MenuItem, Select, Stack, Switch, Typography } from "@mui/material";
import axios from "axios";

import apiRoutes from "../../../constants/api-routes";
import messageLevels from "../../../constants/message-levels";
import messages from "../../../constants/messages";
import pages from "../../../constants/pages";
import validateDomain from "../../../helpers/validateDomain";
import useAuthHeader from "../../../hooks/useAuthHeader";
import useAuthorization from "../../../hooks/useAuthorization";
import useCoAppNavigation from "../../../hooks/useCoAppNavigation";
import useDynamicContainerHeight from "../../../hooks/useDynamicContainerHeight";
import useToastAlert from "../../../hooks/useToastAlert";
import { selectUser } from "../../../redux/userSlice";
import {
	selectOrganizationIdentityProvider,
	selectOrganizationSsoAndScim,
	setOrganizationSsoAndScim
} from "../../../redux/userSlice";
import CoAppMultiSelect from "../../global/components/inputs/CoAppMultiSelect";
import CoAppConfirmationModal from "../../global/components/modals/CoAppConfirmationModal";
import CoAppDestructiveConfirmationModal from "../../global/components/modals/CoAppDestructiveConfirmationModal";
import CoAppKillswitchModal from "../../global/components/modals/CoAppKillswitchModal";
import { CoAppActionButton, CoAppLightTooltip, CoAppWarningIcon, SsoDomainContainer } from "../../global/styled/global.styled";
import { WorkstationSwitch } from "../workstations/styled/workstation.styled";

import SsoScimWizardPreConfigurationModal from "./scim-wizard/SsoScimWizardPreConfigurationModal";
import {
	OrganizationManagementContainer,
	OrganizationManagementStack} from "./styled/organization-management.styled";

const preSsoScimSetupInfo = [
	messages.SCIM_SETUP_MESSAGE_ONE,
	messages.SCIM_SETUP_MESSAGE_TWO,
	messages.SCIM_SETUP_MESSAGE_THREE,
	messages.SCIM_SETUP_MESSAGE_FOUR,
	messages.SCIM_SETUP_MESSAGE_FIVE
];

export default function OrganizationManagement() {
	const isAuthorizedForSsoAndScim = useAuthorization(["MANAGE_SSOSCIM"], false);
	const isAuthorizedForFailedSyncBehavior = useAuthorization(["MANAGE_ORG_FAILED_SYNC_BEHAVIOR"], false);
	const isAuthorizedForForceLogin = useAuthorization(["MANAGE_ORG_FORCELOGIN"], false);
	const isAuthorizedForKillSwitch = useAuthorization(["MANAGE_ORG_KILLSWITCH"], false);
	const isAuthorizedForInUseDomains = useAuthorization(["MANAGE_ORG_DOMAINS"], false);
	const isAuthorizedForExecutionDataEncryption = useAuthorization(["MANAGE_ORG_EXECUTION_DATA_ENCRYPTION"], false);
	const isAuthorizedForFirefoxBlocker = useAuthorization(["MANAGE_ORG_FIREFOX_BLOCKER"], false);
	const isAuthorizedForOnboardingStatus = useAuthorization(["MANAGE_ORG_ONBOARDING"], false);

	const { containerRef, containerHeight } = useDynamicContainerHeight();

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const authHeader = useAuthHeader();
	const { handleToastAlert } = useToastAlert();
	const { openScimWizard } = useCoAppNavigation();
	const currentUser = useSelector(selectUser);
	const identityProviderName = useSelector(selectOrganizationIdentityProvider);
	const [organization, setOrganization] = useState(null);
	const organizationSsoAndScim = useSelector(selectOrganizationSsoAndScim);
	const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
	const [disableSsoConfirmationOpen, setDisableSsoConfirmationOpen] = useState(false);
	const [ssoAndScimConfiguredDate, setSsoAndScimConfiguredDate] = useState(null);
	const [forceLogin, setForceLogin] = useState(false);
	const [killSwitch, setKillSwitch] = useState(false);
	const [firefoxBlockerActive, setFirefoxBlockerActive] = useState(false);
	const [executionDataEncrypted, setExecutionDataEncrypted] = useState(false);
	const [companyDomains, setCompanyDomains] = useState([]);
	const [domainError, setDomainError] = useState("");
	const [inUseDomains, setInUseDomains] = useState([]);
	const [onboardingStatus, setOnboardingStatus] = useState(false);

	const [failedSyncBehavior, setFailedSyncBehavior] = useState("sleep");
	const [forceLoginIsToggled, setForceLoginIsToggled] = useState(false);
	const [killSwitchIsToggled, setKillSwitchIsToggled] = useState(false);
	const [executionDataEncryptedIsToggled, setExecutionDataEncryptedIsToggled] = useState(false);
	const [firefoxBlockerIsToggled, setFirefoxBlockerIsToggled] = useState(false);
	const [onboardingStatusIsToggled, setOnboardingStatusIsToggled] = useState(false);

	let blocker = useBlocker(
		() =>
			failedSyncBehavior !== organization?.failedSyncBehavior
	);

	const unblock = (e) => {
		if (e.type === "unload") {
			e.preventDefault();
		}
		if (blocker.state === "blocked") {
			blocker.proceed();
		}
	};

	const handleCancelExit = () => {
		blocker.reset();
	};

	const initOrganization = () => {
		axios.get(apiRoutes.getOrganization(currentUser.organizationId), {
			headers: authHeader
		}).then((res) => {
			setOrganization(res.data);
			setInUseDomains(res.data.inUseDomains);
			setCompanyDomains(res.data.domains.map(domain => domain.name));
			setForceLogin(res.data.forceLoginActive);
			setKillSwitch(res.data.killSwitchActive);
			setFailedSyncBehavior(res.data.failedSyncBehavior);
			setSsoAndScimConfiguredDate(res.data.ssoAndScimConfiguredOn);
			setExecutionDataEncrypted(res.data.executionDataEncrypted);
			setFirefoxBlockerActive(res.data.firefoxBlockerActive);
			setOnboardingStatus(res.data.isOnboarding);
		});
	};

	const configureSsoAndScim = () => {
		openScimWizard();
	};

	const confirmSsoAndScimConfiguration = () => {
		setConfirmationDialogOpen(true);
	};

	const handleFailedSyncBehaviorChange = (e) => {
		setFailedSyncBehavior(e.target.value);
	};

	const handleToggleDisableSsoAndScim = () => {
		setDisableSsoConfirmationOpen(!disableSsoConfirmationOpen);
	};

	const disableSsoAndScim = () => {
		axios.delete(apiRoutes.ssoAndScim(currentUser.organizationId), { headers: authHeader })
			.then(() => {
				navigate(pages.organizationManagement);
				dispatch(setOrganizationSsoAndScim(false));
				handleToggleDisableSsoAndScim();
				handleToastAlert(messageLevels.INFO, messages.SSO_AND_SCIM_DISABLED_SUCCESS_TOAST_MSG);
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.SSO_AND_SCIM_DISABLED_ERROR_TOAST_MSG);
			});
	};

	const updateOrganizationState = (attribute, value) => {
		switch (attribute) {
			case "failedSyncBehavior":
				setFailedSyncBehavior(value);
				break;
			case "killSwitchActive":
				setKillSwitch(value);
				handleKillSwitchToggle();
				break;
			case "forceLoginActive":
				setForceLogin(value);
				handleForceLoginSwitchToggle();
				break;
			case "executionDataEncrypted":
				setExecutionDataEncrypted(value);
				handleExecutionDataEncryptedToggle();
				break;
			case "firefoxBlockerActive":
				setFirefoxBlockerActive(value);
				handleFirefoxBlockerToggle();
				break;
			case "onboardingStatus":
				setOnboardingStatus(value);
				handleOnboardingStatusToggle();
				break;
			default:
		}
	};

	const handleSimpleOrganizationPatch = async (newData, attribute, errorName) => {
		try {
			const data = {
				...newData,
				patchType: "simple"
			};
			await axios.patch(apiRoutes.patchOrganization(currentUser.organizationId), data, { headers: authHeader });
			updateOrganizationState(attribute, newData[attribute]);
			initOrganization();
		} catch (e) {
			handleToastAlert(messageLevels.ERROR, messages.ORGANIZATION_SIMPLE_PATCH_ERROR_TOAST_MSG(errorName));
		}
	};

	const handleOrganizationDomainsPatch = async (_, value) => {
		const lastDomain = value[value.length - 1];

		const domainAlreadyExists = inUseDomains.find(domain => domain.name.toLowerCase() === lastDomain?.toLowerCase() && domain.organizationId !== organization.id);
		const isValidOrEmpty = validateDomain(lastDomain) || !lastDomain;
		if (isValidOrEmpty && !domainAlreadyExists) {
			try {
				setCompanyDomains([...new Set(value)]);
				setDomainError("");
				const data = {
					patchType: "domains",
					direction: "all",
					tenantId: organization.ssoMetadata?.tenantId,
					emailClaimName: organization.ssoMetadata?.attributeMappings?.email,
					givenNameClaimName: organization.ssoMetadata?.attributeMappings?.givenName,
					surnameClaimName: organization.ssoMetadata?.attributeMappings?.familyName,
					domains: [...new Set(value)],
					metadataUrl: organization.ssoMetadata?.metadataUrl,
				};
				await axios.patch(apiRoutes.patchOrganization(currentUser.organizationId), data, { headers: authHeader });
				handleToastAlert(messageLevels.SUCCESS, messages.ORGANIZATION_DOMAIN_PATCH_SUCCESS_TOAST_MSG);
			} catch (e) {
				console.log(e);
				setCompanyDomains(companyDomains);
				handleToastAlert(messageLevels.ERROR, messages.ORGANIZATION_DOMAIN_ERROR_TOAST_MSG);
			}
		} else {
			if (domainAlreadyExists) {
				setDomainError(`"${lastDomain}" is already in use`);
			} else {
				setDomainError(`"${lastDomain}" is not a valid domain`);
			}
		}
	};

	const handleForceLoginSwitchToggle = () => {
		setForceLoginIsToggled(!forceLoginIsToggled);
	};

	const handleKillSwitchToggle = () => {
		setKillSwitchIsToggled(!killSwitchIsToggled);
	};

	const handleOnboardingStatusToggle = () => {
		setOnboardingStatusIsToggled(!onboardingStatusIsToggled);
	};

	const handleExecutionDataEncryptedToggle = () => {
		setExecutionDataEncryptedIsToggled(!executionDataEncryptedIsToggled);
	};

	const handleFirefoxBlockerToggle = () => {
		setFirefoxBlockerIsToggled(!firefoxBlockerIsToggled);
	};

	const syncUsers = () => {
		axios.post(apiRoutes.sync(currentUser.organizationId), {}, {
			headers: authHeader
		}).then(() => {
			handleToastAlert(messageLevels.SUCCESS, messages.SYNC_ORGANIZATION_STARTED_TOAST_MSG);
		}).catch((err) => {
			handleToastAlert(messageLevels.ERROR, messages.SYNC_ORGANIZATION_FAILED_TO_START_TOAST_MSG);
		});
	};

	useEffect(() => {
		if (isAuthorizedForFailedSyncBehavior || isAuthorizedForInUseDomains || isAuthorizedForForceLogin || 
			isAuthorizedForKillSwitch || isAuthorizedForSsoAndScim || isAuthorizedForFirefoxBlocker || isAuthorizedForOnboardingStatus) {
			initOrganization();
		}
	}, []);

	return (
		<>
			<OrganizationManagementContainer ref={containerRef} height={containerHeight}>
				<OrganizationManagementStack spacing={2}>
					<Typography variant="h1" color="text.primary">{currentUser.organizationName} Organization Settings</Typography>
					<Typography variant="body1" color="text.primary">Manage organization-wide settings.</Typography>
					<Divider sx={{ width: { xxl: "50%", xl: "60%", lg: "60%", md: "70%" } }} />
					{
						isAuthorizedForSsoAndScim &&
					<>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
							{
								organizationSsoAndScim ?
									<>
										<Typography variant="h3" color="text.primary">SSO + SCIM</Typography>
										<IconButton
											onClick={syncUsers}
											color="primary"
										>
											<Sync />
										</IconButton>
										<CoAppLightTooltip title="Deactivate SSO + SCIM" placement="right">
											<Switch
												checked={true}
												onChange={handleToggleDisableSsoAndScim}
											/>
										</CoAppLightTooltip>
									</>
									:
									<CoAppActionButton sx={{ width: "172px" }} onClick={confirmSsoAndScimConfiguration}>Configure SSO + SCIM</CoAppActionButton>
							}
						</Stack>

						<Typography variant="body1" color="text.primary" width={{ xxl: "50%", xl: "60%", lg: "60%", md: "70%" }} sx={{ paddingLeft: "10px" }}>
							{
								organizationSsoAndScim ?
									messages.SSO_AND_SCIM_CONFIGURED_MSG(identityProviderName) :
									messages.SSO_AND_SCIM_NOT_CONFIGURED_MSG
							}
						</Typography>
						{
							isAuthorizedForInUseDomains && organizationSsoAndScim &&
									<>
										<SsoDomainContainer>
											<CoAppMultiSelect
												label="Company domains"
												options={[]}
												onChange={handleOrganizationDomainsPatch}
												value={companyDomains}
												helperText={domainError !== "" ? domainError : ""}
												error={domainError !== ""}
												isFreeSolo={true}
												disableClearable={true}
											/>
										</SsoDomainContainer>
									</>
						}
					</>
					}
					{
						isAuthorizedForKillSwitch &&
						<>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
								<CoAppWarningIcon />
								<Typography variant="h3" color="text.primary">Organization-Wide Kill Switch</Typography>
								<WorkstationSwitch
									checked={killSwitch}
									onChange={handleKillSwitchToggle}
								/>
							</Stack>
							<Typography variant="body1" color={killSwitch ? "text.error" : "text.primary"} sx={{ paddingLeft: "10px" }}>
								{
									killSwitch ?
										messages.KILLSWITCH_ENABLED_MSG :
										messages.KILLSWITCH_DISABLED_MSG
								}
							</Typography>
							<Divider sx={{ width: { xxl: "50%", xl: "60%", lg: "60%", md: "70%" } }} />
						</>
					}
					{
						isAuthorizedForForceLogin &&
						<>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
								<Typography variant="h3" color="text.primary">Organization-Wide Force Login</Typography>
								<Switch
									checked={forceLogin}
									onChange={handleForceLoginSwitchToggle}
								/>
							</Stack>
							<Typography variant="body1" color="text.primary" sx={{ paddingLeft: "10px" }}>
								This forces users to be logged into CoApp while using AcuityLogic.
							</Typography>
							<Divider sx={{ width: { xxl: "50%", xl: "60%", lg: "60%", md: "70%" } }} />
						</>
					}
					{
						isAuthorizedForExecutionDataEncryption &&
						<>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
								<Typography variant="h3" color="text.primary">Organization-Wide Execution Data Encryption</Typography>
								<Switch
									checked={executionDataEncrypted}
									onChange={handleExecutionDataEncryptedToggle}
								/>
							</Stack>
							<Typography variant="body1" color="text.primary" sx={{ paddingLeft: "10px" }}>
							When enabled, all execution data is encrypted.
							</Typography>
							<Divider sx={{ width: { xxl: "50%", xl: "60%", lg: "60%", md: "70%" } }} />
						</>
					}
					{
						isAuthorizedForFirefoxBlocker &&
						<>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
								<Typography variant="h3" color="text.primary">Organization-Wide Firefox Blocker</Typography>
								<Switch
									checked={firefoxBlockerActive}
									onChange={handleFirefoxBlockerToggle}
								/>
							</Stack>
							<Typography variant="body1" color="text.primary" sx={{ paddingLeft: "10px" }}>
							When enabled, all users accessing a configured platform will be blocked from using Firefox as their browser.
							</Typography>
							<Divider sx={{ width: { xxl: "50%", xl: "60%", lg: "60%", md: "70%" } }} />
						</>
					}
					{
						isAuthorizedForFailedSyncBehavior &&
						<>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
								<Typography variant="h3" color="text.primary">Browser Extension Settings</Typography>
							</Stack>
							<Typography variant="body1" color="text.primary" sx={{ paddingLeft: "10px" }}>
						How should the Browser Extension react in the event of not being able to connect to CoApp?
							</Typography>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
								<FormControl>
									<Select
										onChange={handleFailedSyncBehaviorChange}
										value={failedSyncBehavior}
										sx={{ width: "500px" }}
									>
										<MenuItem value="sleep">Immediately go to sleep.</MenuItem>
										<MenuItem value="doNothing">Continue running rules.</MenuItem>
									</Select>
							
								</FormControl>
								<CoAppActionButton onClick={() => handleSimpleOrganizationPatch({ failedSyncBehavior }, "failedSyncBehavior", "Failed Sync Behavior")} disabled={failedSyncBehavior === organization?.failedSyncBehavior}>Save</CoAppActionButton>
							</Stack>
						</>
					}
					{
						isAuthorizedForOnboardingStatus &&
						<>
							<Stack direction="row" spacing={1} alignItems="center" sx={{ paddingLeft: "10px" }}>
								<Typography variant="h3" color="text.primary">Onboarding</Typography>
								<Switch
									checked={onboardingStatus}
									onChange={handleOnboardingStatusToggle}
								/>
							</Stack>
							<Typography variant="body1" color="text.primary" sx={{ paddingLeft: "10px" }}>
							When the Organization is in an onboarding state, new workstations introduced will automatically
							have the kill switch turned ON.
							</Typography>
							<Divider sx={{ width: { xxl: "50%", xl: "60%", lg: "60%", md: "70%" } }} />
						</>
					}
				</OrganizationManagementStack>
			</OrganizationManagementContainer>
			<SsoScimWizardPreConfigurationModal
				dialogOpen={confirmationDialogOpen}
				confirmationTitle="Configure SSO + SCIM"
				confirmationMessage="Before you begin:"
				confirmationListItems={preSsoScimSetupInfo}
				confirmClickHandler={configureSsoAndScim}
				cancelClickHandler={() => setConfirmationDialogOpen(false)}
			/>
			<CoAppKillswitchModal
				dialogOpen={killSwitchIsToggled && !killSwitch}
				dialogTitle="Activate organization-wide kill switch?"
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_KILLSWITCH_MSG("disable")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ killSwitchActive: true }, "killSwitchActive", "Kill Switch")}
				cancelClickHandler={handleKillSwitchToggle}
				actionText="Activate Kill Switch"
			/>
			<CoAppConfirmationModal
				dialogOpen={killSwitchIsToggled && killSwitch}
				dialogTitle="Disable organization-wide kill switch?"
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_KILLSWITCH_MSG("enable")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ killSwitchActive: false }, "killSwitchActive", "Kill Switch")}
				cancelClickHandler={handleKillSwitchToggle}
				actionText="Disable Kill Switch"
			/>
			<CoAppConfirmationModal
				dialogOpen={forceLoginIsToggled}
				dialogMessage={forceLogin ? messages.ORGANIZTION_WIDE_FORCE_LOGIN_ACTIVATE_MSG : messages.ORGANIZTION_WIDE_FORCE_LOGIN_DEACTIVATE_MSG}
				dialogTitle={`${forceLogin ? "Deactivate" : "Activate"} Organization-Wide Force Login?`}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ forceLoginActive: !forceLogin }, "forceLoginActive", "Force Login")}
				cancelClickHandler={handleForceLoginSwitchToggle}
				actionText={forceLogin ? "Deactivate" : "Activate"}
			/>
			<CoAppConfirmationModal
				dialogOpen={executionDataEncryptedIsToggled}
				dialogTitle="Disable organization-wide execution data encryption?"
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_EXECUTION_DATA_ENCRYPTION_MSG(executionDataEncrypted ? "deactivate" : "activate")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ executionDataEncrypted: !executionDataEncrypted }, "executionDataEncrypted", "Execution Data Encryption")}
				cancelClickHandler={handleExecutionDataEncryptedToggle}
				actionText={executionDataEncrypted ? "Deactivate" : "Activate"}
			/>
			<CoAppConfirmationModal
				dialogOpen={firefoxBlockerIsToggled}
				dialogTitle={`${firefoxBlockerActive ? "Deactivate" : "Activate"} Organization-Wide Firefox Blocker?`}
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_FIREFOX_BLOCKER_MSG(firefoxBlockerActive ? "deactivate" : "activate")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ firefoxBlockerActive: !firefoxBlockerActive }, "firefoxBlockerActive", "Firefox Blocker")}
				cancelClickHandler={handleFirefoxBlockerToggle}
				actionText={firefoxBlockerActive ? "Deactivate" : "Activate"}
			/>
			<CoAppConfirmationModal
				dialogOpen={onboardingStatusIsToggled}
				dialogTitle={`${onboardingStatus ? "Deactivate" : "Activate"} Onboarding?`}
				dialogMessage={messages.TOGGLE_ORGANIZATION_WIDE_ONBOARDING_MSG(onboardingStatus ? "deactivate" : "activate")}
				confirmClickHandler={() => handleSimpleOrganizationPatch({ isOnboarding: !onboardingStatus }, "onboardingStatus", "Onboarding")}
				cancelClickHandler={handleOnboardingStatusToggle}
				actionText={onboardingStatus ? "Deactivate" : "Activate"}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={disableSsoConfirmationOpen}
				dialogMessage={messages.DISABLE_SSO_AND_SCIM_MSG}
				dialogTitle="Disable SSO + SCIM?"
				confirmClickHandler={disableSsoAndScim}
				cancelClickHandler={handleToggleDisableSsoAndScim}
				actionText="Disable"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={blocker.state === "blocked"}
				dialogTitle="Abandon Organization Settings?"
				dialogMessage="There are unsaved changes on this page. If you leave now, any unsaved changes will be lost."
				confirmClickHandler={unblock}
				cancelClickHandler={handleCancelExit}
				actionText="Confirm"
			/>
		</>
	);
}